/**
 * Netplay session in the browser (see docs/NETPLAY.md).
 *
 * Lives outside React so the room and the peer connections survive moving from
 * the lobby to the player page. Every pair of members gets one RTCPeerConnection
 * with two pre-negotiated data channels: `control` (reliable, ordered) and
 * `inputs` (unordered, no retransmits). The member with the smaller user id
 * makes the offer, so offers never cross. Signaling goes through the REPLAY
 * server (POST /signal, `netplay` server-sent events).
 */
import { useSyncExternalStore } from 'react';
import { api, errorMessage } from '../api.ts';
import { onLive } from '../live.ts';

export interface RoomMember {
  userId: string;
  displayName: string;
  port: number | null;
  joinedAt: string;
  /** Link cable rooms: the cartridge this player brings (their own game and save). */
  game: { id: string; title: string; systemId: string } | null;
}
export interface Room {
  id: string;
  /** pads: one console, several pads. link: each player's own Game Boy, joined by a cable. */
  mode: 'pads' | 'link';
  gameId: string;
  gameTitle: string;
  systemId: string;
  hostId: string;
  status: 'lobby' | 'playing';
  members: RoomMember[];
  invited: string[];
  maxPlayers: number;
}
export interface PeerInfo {
  userId: string;
  state: 'connecting' | 'connected' | 'reconnecting' | 'failed';
  /**
   * Round trip measured on the control channel: the median of the last pings, so
   * one late pong (a phone's radio waking up, a busy frame) does not show as lag.
   */
  rttMs: number | null;
  /** Best round trip of the last pings: the network itself, without queues (e.g. during a state transfer). */
  rttBestMs: number | null;
  /** How the bytes travel: straight between players, or through the TURN relay. */
  route: 'direct' | 'relay' | null;
}
export interface NetplayState {
  room: Room | null;
  peers: Record<string, PeerInfo>;
  closedReason: string | null;
  error: string | null;
}
type NetplayEvent =
  | { kind: 'room'; room: Room }
  | { kind: 'closed'; roomId: string; reason: string }
  | { kind: 'signal'; roomId: string; from: string; data: SignalData };
type SignalData = { sdp?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit | null; restart?: boolean };
export type ControlMessage = { t: string; [k: string]: unknown };

interface Peer {
  pc: RTCPeerConnection;
  control: RTCDataChannel;
  inputs: RTCDataChannel;
  offerer: boolean;
  pending: RTCIceCandidateInit[];
  /** Control messages sent before the channel opened: they go out when it does. */
  unsent: (string | ArrayBuffer)[];
  pinger?: ReturnType<typeof setInterval>;
  restarts: number;
  rtts: number[];
}

const CLOSED_TEXT: Record<string, string> = {
  host_left: 'El anfitrión ha cerrado la sala.',
  expired: 'La sala ha caducado por inactividad.',
};

class NetplaySession {
  private state: NetplayState = { room: null, peers: {}, closedReason: null, error: null };
  private listeners = new Set<() => void>();
  private peers = new Map<string, Peer>();
  private me = '';
  private ice: RTCIceServer[] | null = null;
  private offLive: (() => void) | null = null;
  private controlHandlers = new Set<(from: string, msg: ControlMessage) => void>();
  private inputHandlers = new Set<(from: string, data: ArrayBuffer) => void>();
  private binaryHandlers = new Set<(from: string, data: ArrayBuffer) => void>();

  // ---------- store for React ----------
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  snapshot = () => this.state;
  private set(patch: Partial<NetplayState>) {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }
  private setPeer(userId: string, patch: Partial<PeerInfo>) {
    const prev = this.state.peers[userId] ?? { userId, state: 'connecting', rttMs: null, rttBestMs: null, route: null };
    this.set({ peers: { ...this.state.peers, [userId]: { ...prev, ...patch } } });
  }

  // ---------- room actions ----------
  /** Called once the user is known; picks up a room this user is already in (page reload). */
  async init(meId: string) {
    if (this.me === meId) return;
    this.me = meId;
    try {
      const { room } = await api<{ room: Room | null }>('/api/netplay/room');
      if (room) this.enter(room);
    } catch {
      /* offline: no session to resume */
    }
  }

  async host(gameId: string, mode: Room['mode'] = 'pads') {
    const { room } = await api<{ room: Room }>('/api/netplay/rooms', { body: { gameId, mode } });
    this.enter(room);
    return room;
  }

  /** gameId: the cartridge a new player brings to a link room. */
  async join(roomId: string, gameId?: string) {
    const { room } = await api<{ room: Room }>(`/api/netplay/rooms/${roomId}/join`, { body: gameId ? { gameId } : {} });
    this.enter(room);
    return room;
  }

  async leave() {
    const room = this.state.room;
    this.teardown();
    this.set({ room: null, closedReason: null, error: null });
    if (room) await api(`/api/netplay/rooms/${room.id}/leave`, { body: {} }).catch(() => {});
  }

  invite(userId: string) {
    return this.roomCall('invite', { userId });
  }
  setPort(userId: string, port: number | null) {
    return this.roomCall('port', { userId, port });
  }
  start() {
    return this.roomCall('start', {});
  }
  /** Link rooms: bring another cartridge (lobby only). */
  setCartridge(gameId: string) {
    return this.roomCall('cartridge', { gameId });
  }
  private async roomCall(action: string, body: Record<string, string | number | null>) {
    const room = this.state.room;
    if (!room) throw new Error('No estás en ninguna sala');
    const r = await api<{ room: Room }>(`/api/netplay/rooms/${room.id}/${action}`, { body });
    this.applyRoom(r.room);
  }

  // ---------- channels for gameplay (phase 2) ----------
  /**
   * Reliable and ordered. A peer still connecting gets it once its channel opens
   * (a guest that finished loading before reaching the host must not lose its
   * np-ready: the room would wait forever).
   */
  sendControl(to: string | null, msg: ControlMessage) {
    const text = JSON.stringify(msg);
    for (const [id, p] of this.peers) if (to === null || id === to) this.sendOnControl(p, text);
  }
  /** Reliable, ordered binary data (e.g. the start state, in chunks). */
  sendControlBinary(to: string | null, data: ArrayBuffer) {
    for (const [id, p] of this.peers) if (to === null || id === to) this.sendOnControl(p, data);
  }
  private sendOnControl(p: Peer, data: string | ArrayBuffer) {
    if (p.control.readyState === 'open') p.control.send(data as string);
    else if (p.control.readyState === 'connecting') p.unsent.push(data);
  }
  /** Bytes queued on the control channel to a peer (to pace large transfers). */
  controlBuffered(to: string) {
    return this.peers.get(to)?.control.bufferedAmount ?? 0;
  }
  onControlBinary(fn: (from: string, data: ArrayBuffer) => void) {
    this.binaryHandlers.add(fn);
    return () => this.binaryHandlers.delete(fn);
  }
  sendInputs(to: string | null, data: ArrayBuffer) {
    for (const [id, p] of this.peers) if ((to === null || id === to) && p.inputs.readyState === 'open') p.inputs.send(data);
  }
  onControl(fn: (from: string, msg: ControlMessage) => void) {
    this.controlHandlers.add(fn);
    return () => this.controlHandlers.delete(fn);
  }
  onInputs(fn: (from: string, data: ArrayBuffer) => void) {
    this.inputHandlers.add(fn);
    return () => this.inputHandlers.delete(fn);
  }

  // ---------- internals ----------
  private enter(room: Room) {
    if (!this.offLive) this.offLive = onLive('netplay', (e) => void this.onEvent(e as NetplayEvent));
    this.set({ closedReason: null, error: null });
    this.applyRoom(room);
  }

  private applyRoom(room: Room) {
    if (this.state.room && this.state.room.id !== room.id) this.teardown(false);
    this.set({ room });
    const others = room.members.map((m) => m.userId).filter((id) => id !== this.me);
    for (const id of this.peers.keys()) if (!others.includes(id)) this.dropPeer(id);
    for (const id of others) if (!this.peers.has(id)) void this.connect(id);
  }

  private async onEvent(e: NetplayEvent) {
    const room = this.state.room;
    if (!room) return;
    if (e.kind === 'room' && e.room.id === room.id) this.applyRoom(e.room);
    else if (e.kind === 'closed' && e.roomId === room.id) {
      this.teardown();
      this.set({ room: null, closedReason: CLOSED_TEXT[e.reason] ?? 'La sala se ha cerrado.' });
    } else if (e.kind === 'signal' && e.roomId === room.id) await this.onSignal(e.from, e.data);
  }

  private async iceServers() {
    if (!this.ice) this.ice = (await api<{ iceServers: RTCIceServer[] }>('/api/netplay/ice')).iceServers;
    return this.ice;
  }

  private signal(to: string, data: SignalData) {
    const room = this.state.room;
    if (!room) return;
    void api(`/api/netplay/rooms/${room.id}/signal`, { body: { to, data } }).catch((e) => this.set({ error: errorMessage(e) }));
  }

  private async connect(userId: string) {
    const offerer = this.me < userId;
    const pc = new RTCPeerConnection({ iceServers: await this.iceServers() });
    const control = pc.createDataChannel('control', { negotiated: true, id: 0 });
    const inputs = pc.createDataChannel('inputs', { negotiated: true, id: 1, ordered: false, maxRetransmits: 0 });
    inputs.binaryType = 'arraybuffer';
    control.binaryType = 'arraybuffer';
    const peer: Peer = { pc, control, inputs, offerer, pending: [], unsent: [], restarts: 0, rtts: [] };
    // A newer room update may have removed this member while ICE servers loaded.
    if (this.peers.has(userId) || !this.state.room?.members.some((m) => m.userId === userId)) return pc.close();
    this.peers.set(userId, peer);
    this.setPeer(userId, { state: 'connecting' });

    pc.onicecandidate = (e) => this.signal(userId, { candidate: e.candidate?.toJSON() ?? null });
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === 'connected') {
        peer.restarts = 0;
        this.setPeer(userId, { state: 'connected' });
        void this.updateRoute(userId, pc);
      } else if (s === 'disconnected') this.setPeer(userId, { state: 'reconnecting' });
      else if (s === 'failed') {
        // The offerer retries with an ICE restart a few times before giving up.
        if (offerer && peer.restarts < 3) {
          peer.restarts++;
          this.setPeer(userId, { state: 'reconnecting' });
          void this.offer(userId, peer, true);
        } else this.setPeer(userId, { state: 'failed' });
      }
    };
    control.onopen = () => {
      for (const data of peer.unsent.splice(0)) control.send(data as string);
      peer.pinger = setInterval(() => {
        if (control.readyState === 'open') control.send(JSON.stringify({ t: 'ping', at: performance.now() }));
      }, 1000);
    };
    control.onmessage = (e) => {
      if (typeof e.data !== 'string') {
        for (const fn of this.binaryHandlers) fn(userId, e.data as ArrayBuffer);
        return;
      }
      let msg: ControlMessage;
      try {
        msg = JSON.parse(e.data as string);
      } catch {
        return;
      }
      if (msg.t === 'ping') control.send(JSON.stringify({ t: 'pong', at: msg.at }));
      else if (msg.t === 'pong') {
        const rtt = performance.now() - (msg.at as number);
        peer.rtts = [...peer.rtts.slice(-9), rtt];
        const sorted = [...peer.rtts].sort((a, b) => a - b);
        this.setPeer(userId, { rttMs: Math.round(sorted[Math.floor(sorted.length / 2)]!), rttBestMs: Math.round(sorted[0]!) });
      } else for (const fn of this.controlHandlers) fn(userId, msg);
    };
    inputs.onmessage = (e) => {
      for (const fn of this.inputHandlers) fn(userId, e.data as ArrayBuffer);
    };
    if (offerer) await this.offer(userId, peer);
  }

  private async offer(userId: string, peer: Peer, restart = false) {
    try {
      const offer = await peer.pc.createOffer(restart ? { iceRestart: true } : undefined);
      await peer.pc.setLocalDescription(offer);
      this.signal(userId, { sdp: peer.pc.localDescription!.toJSON() });
    } catch (e) {
      this.set({ error: errorMessage(e) });
    }
  }

  private async onSignal(from: string, data: SignalData) {
    let peer = this.peers.get(from);
    if (!peer) {
      await this.connect(from);
      peer = this.peers.get(from);
      if (!peer) return;
    }
    const pc = peer.pc;
    try {
      if (data.sdp) {
        await pc.setRemoteDescription(data.sdp);
        for (const c of peer.pending.splice(0)) await pc.addIceCandidate(c);
        if (data.sdp.type === 'offer') {
          await pc.setLocalDescription(await pc.createAnswer());
          this.signal(from, { sdp: pc.localDescription!.toJSON() });
        }
      } else if (data.candidate !== undefined) {
        if (!data.candidate) return;
        if (pc.remoteDescription) await pc.addIceCandidate(data.candidate);
        else peer.pending.push(data.candidate);
      }
    } catch (e) {
      this.set({ error: errorMessage(e) });
    }
  }

  /** Direct or relayed, from the selected candidate pair. */
  private async updateRoute(userId: string, pc: RTCPeerConnection) {
    try {
      const stats = await pc.getStats();
      let pairId: string | undefined;
      stats.forEach((s) => {
        if (s.type === 'transport' && s.selectedCandidatePairId) pairId = s.selectedCandidatePairId;
      });
      const pair = pairId ? stats.get(pairId) : undefined;
      const local = pair ? stats.get(pair.localCandidateId) : undefined;
      const remote = pair ? stats.get(pair.remoteCandidateId) : undefined;
      const relay = local?.candidateType === 'relay' || remote?.candidateType === 'relay';
      this.setPeer(userId, { route: pair ? (relay ? 'relay' : 'direct') : null });
    } catch {
      /* stats are informative only */
    }
  }

  private dropPeer(userId: string) {
    const p = this.peers.get(userId);
    if (p) {
      clearInterval(p.pinger);
      p.pc.close();
    }
    this.peers.delete(userId);
    const { [userId]: _gone, ...rest } = this.state.peers;
    this.set({ peers: rest });
  }

  private teardown(stopLive = true) {
    for (const id of [...this.peers.keys()]) this.dropPeer(id);
    if (stopLive) {
      this.offLive?.();
      this.offLive = null;
    }
  }
}

export const netplay = new NetplaySession();

/**
 * How a round trip feels in a game: rollback and the input delay hide up to
 * ~150 ms (a phone on mobile data through the relay plays fine at 120 ms).
 */
export function latencyTone(ms: number): 'ok' | 'warn' | 'bad' {
  return ms < 150 ? 'ok' : ms < 250 ? 'warn' : 'bad';
}

export function useNetplay(): NetplayState {
  return useSyncExternalStore(netplay.subscribe, netplay.snapshot);
}
